import type { ViewPrimitive, BladeContext } from '../registry';
import { BladeLoading, BladeError, BladeDenied, BladeEmpty, forcedBladeState } from '../BladeStates';
import { useBladeList } from '../BladeDataSource';

// List view-primitive (HUB-276/277). Rows come from the BladeDataSource (live
// @hub/api on /w, mock in the preview); selecting a row drills into the Record
// detail. Content layer — brand utilities, no shell.css coupling. Loading / empty
// / error / denied delegate to the state catalog.

function ListBody({ ctx }: { ctx: BladeContext }) {
  const { status, data } = useBladeList(ctx.descriptor);

  if (status === 'loading') return <BladeLoading />;
  if (status === 'error') return <BladeError />;
  if (status === 'denied') return <BladeDenied />;

  const rows = data?.rows ?? [];
  if (rows.length === 0) {
    return <BladeEmpty title="No items" description="This worklist has no items." />;
  }

  return (
    <ul role="list" className="flex flex-col">
      {rows.map((row) => (
        <li key={row.id}>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2.5 text-left transition-colors hover:bg-muted"
            onClick={(e) => {
              e.stopPropagation();
              ctx.openChild({
                key: 'record',
                title: row.title,
                eyebrow: 'Record',
                entityId: row.id,
                width: 'med',
              });
            }}
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-foreground">{row.title}</div>
              <div className="font-mono text-xs text-muted-foreground">{row.id}</div>
            </div>
            {row.meta && <span className="shrink-0 text-xs text-muted-foreground">{row.meta}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

export const listPrimitive: ViewPrimitive = {
  key: 'list',
  render: (ctx) => {
    // Forced states (params.state) short-circuit before the data hook so demos /
    // tests / deep-links don't need a live source.
    const forced = forcedBladeState(ctx.descriptor);
    if (forced === 'loading') return <BladeLoading />;
    if (forced === 'error') return <BladeError />;
    if (forced === 'denied') return <BladeDenied />;
    if (forced === 'empty') {
      return <BladeEmpty title="No items" description="This worklist has no items." />;
    }
    return <ListBody ctx={ctx} />;
  },
};
